import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Send, TrendingUp, FileText, Bot, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MD = ({ children }) => (
  <div className="prose-sm max-w-none [&_h1]:font-heading [&_h2]:font-heading [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h3]:font-medium [&_ul]:list-disc [&_ul]:pl-4 [&_li]:my-0.5 [&_strong]:text-slate-100 [&_table]:w-full [&_table]:my-2 [&_td]:border [&_td]:border-[#334155] [&_td]:px-2 [&_td]:py-1 [&_th]:border [&_th]:border-[#334155] [&_th]:px-2 [&_th]:py-1 [&_th]:text-left text-slate-300">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{String(children || "")}</ReactMarkdown>
  </div>
);

const suggestions = [
  "What's our cash position right now?",
  "Which category is closest to going over budget?",
  "Show me all pending tax filings",
  "Which service line is most profitable?",
];

export default function AIPanel() {
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insBusy, setInsBusy] = useState(false);
  const [summary, setSummary] = useState("");
  const [sumBusy, setSumBusy] = useState(false);
  const [sumErr, setSumErr] = useState("");

  const ask = async (text) => {
    const question = text ?? q;
    if (!question.trim()) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/query", { question });
      setMsgs((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI request failed";
      setMsgs((m) => [...m, { role: "ai", text: msg }]);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const loadInsights = async () => {
    setInsBusy(true);
    try {
      const { data } = await api.get("/ai/insights");
      setInsights(data);
    } catch (e) {
      toast.error("Could not load insights");
    } finally {
      setInsBusy(false);
    }
  };

  const genSummary = async () => {
    setSumBusy(true);
    setSumErr("");
    try {
      const { data } = await api.post("/ai/monthly-summary");
      setSummary(data.summary);
    } catch (e) {
      const msg = e?.response?.data?.detail || "The summary took too long to generate. Please try again.";
      setSumErr(msg);
      toast.error("Could not generate summary");
    } finally {
      setSumBusy(false);
    }
  };

  const insightCards = insights
    ? [
        { icon: TrendingUp, t: "30/60/90 Cash Forecast", v: insights.forecast },
        { icon: Sparkles, t: "Budget Overrun Risk", v: insights.budget_risk },
        { icon: TrendingUp, t: "Profitability Trend", v: insights.profitability },
        { icon: FileText, t: "Scheme Matching", v: insights.schemes },
      ]
    : [];

  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          data-testid="ai-panel-trigger"
          className="fixed bottom-6 right-6 z-50 h-14 px-5 rounded-full bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold shadow-lg shadow-black/40 flex items-center gap-2 transition-all active:scale-95"
        >
          <Sparkles className="h-5 w-5" /> AI Assistant
        </button>
      </SheetTrigger>
      <SheetContent className="bg-[#0B1120] border-l border-[#334155] text-slate-100 w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-6 py-5 border-b border-[#334155]">
          <SheetTitle className="text-slate-100 font-heading flex items-center gap-2">
            <Bot className="h-5 w-5 text-[#C9A24B]" /> Finance Intelligence
          </SheetTitle>
        </SheetHeader>

        <Tabs defaultValue="ask" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="mx-6 mt-4 bg-[#1E293B] border border-[#334155] grid grid-cols-3">
            <TabsTrigger value="ask" data-testid="ai-tab-ask" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120] text-xs">Ask</TabsTrigger>
            <TabsTrigger value="insights" data-testid="ai-tab-insights" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120] text-xs">Insights</TabsTrigger>
            <TabsTrigger value="summary" data-testid="ai-tab-summary" className="data-[state=active]:bg-[#C9A24B] data-[state=active]:text-[#0B1120] text-xs">Summary</TabsTrigger>
          </TabsList>

          <TabsContent value="ask" className="flex-1 flex flex-col overflow-hidden m-0 px-6 pb-6 pt-4">
            <div className="flex-1 overflow-y-auto space-y-3 pr-1">
              {msgs.length === 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-400 mb-3">Ask anything about your finances:</p>
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => ask(s)} data-testid="ai-suggestion" className="w-full text-left text-sm px-3 py-2.5 rounded-md border border-[#334155] bg-[#1E293B] hover:border-[#C9A24B]/50 text-slate-300 transition-all">
                      {s}
                    </button>
                  ))}
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`text-sm rounded-lg px-3 py-2.5 ${m.role === "user" ? "bg-[#C9A24B]/15 border border-[#C9A24B]/25 ml-8" : "bg-[#1E293B] border border-[#334155] mr-4"}`}>
                  {m.role === "ai" ? <MD>{m.text}</MD> : m.text}
                </div>
              ))}
              {busy && <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Thinking…</div>}
            </div>
            <div className="flex gap-2 mt-4">
              <Input
                data-testid="ai-query-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
                placeholder="Ask the Finance AI…"
                className="bg-[#1E293B] border-[#334155] focus:border-[#C9A24B]"
              />
              <Button data-testid="ai-query-send" onClick={() => ask()} disabled={busy} className="bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="insights" className="flex-1 overflow-y-auto m-0 px-6 pb-6 pt-4 space-y-3">
            <Button data-testid="ai-load-insights" onClick={loadInsights} disabled={insBusy} className="w-full bg-[#1E293B] border border-[#334155] hover:border-[#C9A24B]/50 text-slate-200">
              {insBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analysing…</> : <><Sparkles className="h-4 w-4 mr-2 text-[#C9A24B]" /> Generate Intelligence</>}
            </Button>
            {insightCards.map((c) => (
              <div key={c.t} data-testid="ai-insight-card" className="rounded-lg border border-[#334155] bg-[#1E293B] p-4">
                <div className="flex items-center gap-2 mb-1.5"><c.icon className="h-4 w-4 text-[#C9A24B]" /><span className="text-sm font-medium">{c.t}</span></div>
                <p className="text-sm text-slate-400 leading-relaxed">{String(c.v || "—")}</p>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="summary" className="flex-1 overflow-y-auto m-0 px-6 pb-6 pt-4 space-y-3">
            <p className="text-sm text-slate-400">Auto-draft the month-end Finance briefing for the Founder.</p>
            <Button data-testid="ai-gen-summary" onClick={genSummary} disabled={sumBusy} className="w-full bg-[#C9A24B] hover:bg-[#D4AF37] text-[#0B1120] font-semibold">
              {sumBusy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting…</> : <><FileText className="h-4 w-4 mr-2" /> Generate Monthly Summary</>}
            </Button>
            {sumErr && <div data-testid="ai-summary-error" className="rounded-lg border border-rose-600/30 bg-rose-600/8 p-4 text-sm text-rose-300">{sumErr}</div>}
            {summary && <div data-testid="ai-summary-output" className="rounded-lg border border-[#334155] bg-[#1E293B] p-4 text-sm leading-relaxed"><MD>{summary}</MD></div>}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
